/**
 * What the قرعه‌کشی landing teaches, and where on the real screen it is.
 *
 * The claims live here rather than on the intro page. An earlier version
 * explained Hamyan twice — a page of value cards, then a tour over a fund the
 * reader had been given no reason to look at — and both were walls of text
 * anchored to nothing. So each topic is one thing Hamyan does for a fund
 * manager, paired with the element on the sample صندوق where that thing is
 * visible. Tapping a topic drops the reader at that element with two or three
 * sentences beside it: they learn it where they can see it, which is also the
 * only place it can be checked.
 *
 * **Titles carry the whole index.** There is no teaser line any more — seven
 * rows of title-plus-subtitle was the same wall of small text in a different
 * shape, and a title that needs a subtitle to make sense is a title that has
 * not been written yet. «کی داده، کی نداده» needs nothing under it.
 *
 * They are also kept short enough to sit on ONE line in a narrow grid cell at
 * 360px. A title that wraps turns a scannable list into a paragraph, which is
 * the thing this index exists to avoid; a test measures the rendered line
 * count at every breakpoint rather than trusting the character count here.
 *
 * **The voice is second-person singular, the way a colleague talks.** These
 * readers run a fund among people they know; «صندوق شما منتقل می‌گردد» is how a
 * bank circular addresses a stranger, and it reads as one.
 *
 * Anchors are CSS selectors resolved on the demo screen, and they point at the
 * ONE control the topic is about — a spotlight over the whole action grid
 * while the card talks about پیامک highlights five things it is not
 * describing. A selector that matches nothing is not fatal: the tour falls
 * back to a centred card, which is right when a product screen is rebuilt
 * under us.
 */

export interface LandingTopic {
    /** stable id — reported to analytics and carried in the URL */
    key: string
    icon: string
    /** short enough to stand alone in the index */
    title: string
    /** two or three sentences, said in context during the tour */
    body: string
    /** the one control on the demo screen this is about */
    anchor?: string
    /** the tab the demo must be on for `anchor` to exist */
    tab?: 'members' | 'transactions' | 'winners'
}

export const TOPICS: ReadonlyArray<LandingTopic> = [
    {
        key: 'ledger',
        icon: '📒',
        title: 'حساب‌ها خودکارن',
        body: 'این سه عدد را کسی وارد نکرده — از روی پرداخت اعضا حساب شده‌اند. تو فقط می‌گویی چه شد: «علی سهمش را داد». جمع و تفریقش با همیان.',
        anchor: '[data-tour="summary"] .rounded-card',
    },
    {
        key: 'draw',
        icon: '🎯',
        title: 'قرعه با یک لمس',
        body: 'همین دکمه. قرعه فقط بین کسانی کشیده می‌شود که هنوز نوبتشان نشده، پس لازم نیست یادت باشد کی برنده شده. نتیجه همان لحظه برای همه ثبت می‌شود.',
        anchor: '[data-tour="actions"] [data-action="draw"]',
    },
    {
        key: 'members',
        icon: '✅',
        title: 'کی داده، کی نداده',
        body: 'روبه‌روی هر اسم نوشته چقدر داده و چقدر مانده — بدون اینکه کسی تیکی بزند. همان کاری که هر دوره در دفتر یا گروه می‌کنی، اینجا خودش انجام شده.',
        anchor: '[data-tour="tabs"]',
        tab: 'members',
    },
    {
        key: 'transparency',
        icon: '👁️',
        title: 'اعضا هم می‌بینن',
        body: 'هر واریز و هر وام اینجا ردیف شده، و این صفحه را همهٔ اعضا هم دارند. یعنی لازم نیست به کسی ثابت کنی پول کجا رفته؛ خودش پیداست.',
        anchor: '[data-tour="tabs"]',
        tab: 'transactions',
    },
    {
        key: 'notify',
        icon: '🔔',
        title: 'دیگه تذکر نمی‌دی',
        body: 'از همین‌جا به اعضا اطلاع می‌رسد، و پیامک خودکار هم هست: تا وام پرداخت شد، تا واریزی نشست، و پیش از سررسید هر قسط. نتیجه‌اش یک چیز است — پول سر وقت می‌رسد.',
        anchor: '[data-tour="actions"] [data-action="bulletins"]',
    },
    {
        key: 'plans',
        icon: '🎁',
        title: 'چقدر خرج داره؟',
        body: 'هرچه تا اینجا دیدی در نسخهٔ رایگان هست. نسخهٔ پیشرفته پیامک، درگاه پرداخت و نگهداری پول نزد همیان را اضافه می‌کند. کدامش به کار صندوقت می‌آید را در تماس با هم مشخص می‌کنیم.',
        anchor: '[data-tour="actions"] [data-action="plans"]',
    },
    {
        key: 'transfer',
        icon: '📦',
        title: 'صندوق خودت، همینه',
        body: 'آنچه دیدی یک صندوق نمونه بود؛ صندوق خودت هم همین شکلی می‌شود. اعضا، سهم‌ها و نوبت‌هایی که تا حالا کشیده‌ای منتقل می‌شوند — از اول ساختن در کار نیست.',
        anchor: '[data-tour="cta"]',
    },
]

export const topicByKey = (key?: string | null): LandingTopic | undefined =>
    TOPICS.find((topic) => topic.key === key)

/** The topic after this one, for «موضوع بعدی». Undefined at the end. */
export const nextTopic = (key: string): LandingTopic | undefined => {
    const index = TOPICS.findIndex((topic) => topic.key === key)
    return index >= 0 ? TOPICS[index + 1] : undefined
}
